import { useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronUp, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';
import { fmtDate, fmtDateLong } from '@/features/orders/lib';
import {
  CALENDAR_KINDS, KIND_LABELS, WAREHOUSE_KINDS, addDays, chipDetail, describeItem, weekStartOf,
  type CalendarItem, type CalendarKind,
} from './calendarItems';
import { kindShown, matchesQuery, pickKind, readFilter, writeFilter, type CalendarFilter } from './calendarFilter';
import { KIND_ICONS, KIND_TONE } from './kindStyle';

/**
 * Five weeks of dated facts: last week, this week and the three after. A
 * month view would spend most of its rows on days already gone or leave
 * nothing behind today; a rolling window always shows a week of what just
 * landed and three or four weeks of what is coming. Every chip is one fact in
 * one shape — kind, vendor, one detail — and opens the journey it belongs to.
 *
 * On a phone the grid is a one-column agenda, where last week is a long scroll
 * above today; there the days before today fold behind one control at the top
 * of the list, so the agenda opens on today.
 *
 * `readOnly` is the warehouse's calendar: chips that explain themselves in a
 * tooltip but open nothing, and pills for the warehouse's kinds only (no
 * Order by). Which items it gets (warehouseItems) is the caller's choice.
 */

const WEEKS = 5;
/** Weeks before the current one that stay in view. */
const PAST_WEEKS = 1;
/** Chips shown in a day before the rest fold behind "+n more"; one kind alone gets more. */
const MAX_CHIPS = 4;
const MAX_CHIPS_ONE_KIND = 6;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Compact nav buttons; min-h-0 must win over the coarse-pointer 44px rule. */
const NAV_BUTTON = 'h-[30px] min-h-0! rounded-[6px]';

const dayDate = (day: string) => new Date(`${day}T00:00:00`);

const CHIP = 'grid min-w-0 gap-px rounded-[5px] bg-(--chip-surface) px-1.5 py-[3px] text-(--chip-ink)';

function Chip({ item, returnTo, hint = true, readOnly = false }: { item: CalendarItem; returnTo: string; hint?: boolean; readOnly?: boolean }) {
  const Icon = KIND_ICONS[item.kind];
  const text = describeItem(item);
  const { status, text: detail } = chipDetail(item);
  // Two lines at most: the vendor, with a count badge only when there is a
  // count; then the kind's one detail (see chipDetail). Nothing reserves room
  // for a fact the item does not have, so a lone vendor name gets the whole
  // width, and a chip with no detail stays one line.
  const body = (
    <>
      <span className="flex min-w-0 items-center gap-1 text-[12px] leading-4">
        <Icon aria-hidden="true" className="size-[11px] shrink-0" />
        <span className="min-w-0 flex-1 truncate font-semibold">{item.vendor}</span>
        {item.count > 1 && (
          <span className="shrink-0 rounded-full bg-[color-mix(in_srgb,currentColor_14%,transparent)] px-1.5 text-[10.5px] leading-4 font-bold tabular-nums">
            {item.count}
          </span>
        )}
      </span>
      {(status || detail) && (
        <span className="truncate pl-[15px] text-[11px] leading-[15px]">
          {status && <span className={cn('font-semibold', item.overdue && 'text-danger-ink')}>{status}</span>}
          {status && detail && <span className="opacity-60"> · </span>}
          {detail && <span className="opacity-80">{detail}</span>}
        </span>
      )}
    </>
  );
  if (readOnly) {
    // Focusable so the tooltip reaches the keyboard too; it opens nothing.
    const plain = <div tabIndex={0} className={cn(CHIP, 'cursor-default', KIND_TONE[item.kind])} data-kind={item.kind} aria-label={text}>{body}</div>;
    return hint ? <Hint content={text} side="bottom">{plain}</Hint> : plain;
  }
  const chip = (
    <Link
      to={item.to}
      state={{ returnTo }}
      className={cn(
        CHIP, 'hover:shadow-[inset_0_0_0_1px_var(--chip-line)]',
        // Late: the app's tone edge in danger, on the kind's own colour.
        'data-[overdue]:pl-2.5 data-[overdue]:shadow-[inset_3px_0_0_var(--tone-danger)] data-[overdue]:hover:shadow-[inset_3px_0_0_var(--tone-danger),inset_0_0_0_1px_var(--chip-line)]',
        KIND_TONE[item.kind]
      )}
      data-kind={item.kind}
      data-overdue={item.overdue || undefined}
      aria-label={text}
    >
      {body}
    </Link>
  );
  return hint ? <Hint content={text} side="bottom">{chip}</Hint> : chip;
}

function DayMore({ day, items, rest, returnTo, readOnly }: { day: string; items: CalendarItem[]; rest: number; returnTo: string; readOnly: boolean }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="self-start rounded-[5px] px-1.5 py-px pointer-coarse:py-1.5 text-[11px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground" aria-label={`${rest} more on ${fmtDateLong(day)}`}>
          +{rest} more
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="px-1.5 pt-1 pb-2 text-[12px] font-semibold">{format(dayDate(day), 'EEEE, MMM d')}</div>
        <div className="grid gap-[3px]">
          {items.map((item) => <Chip key={item.key} item={item} returnTo={returnTo} hint={readOnly} readOnly={readOnly} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** A kind pill, and the "All" pill when `kind` is null. */
const PILL = 'inline-flex items-center gap-[5px] rounded-full border py-[3px] pointer-coarse:py-[7px] pr-[9px] pl-[7px] text-[12px] font-medium aria-[pressed=false]:border-border aria-[pressed=false]:bg-transparent aria-[pressed=false]:text-muted-foreground aria-[pressed=false]:hover:text-foreground';

export function OverviewCalendar({ items, today, readOnly = false }: { items: CalendarItem[]; today: string; readOnly?: boolean }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const universe = readOnly ? WAREHOUSE_KINDS : CALENDAR_KINDS;
  const filter = useMemo(() => readFilter(params, universe), [params, universe]);
  const update = (patch: Partial<CalendarFilter>) =>
    setParams((prev) => writeFilter(prev, { ...readFilter(prev, universe), ...patch }, universe), { replace: true });
  // A journey opened from here comes back to this exact view.
  const returnTo = `/${location.search}`;

  const { weekOffset, kinds, query } = filter;
  const start = addDays(weekStartOf(today), (weekOffset - PAST_WEEKS) * 7);
  const end = addDays(start, WEEKS * 7 - 1);
  const days = useMemo(() => Array.from({ length: WEEKS * 7 }, (_, i) => addDays(start, i)), [start]);
  const matching = useMemo(() => items.filter((i) => matchesQuery(i, query)), [items, query]);
  const inWindow = useMemo(() => matching.filter((i) => i.day >= start && i.day <= end), [matching, start, end]);
  // Pill counts follow the search but not the kinds, so a hidden kind still
  // says how much it holds.
  const kindCounts = useMemo(() => {
    const counts = Object.fromEntries(CALENDAR_KINDS.map((k) => [k, 0])) as Record<CalendarKind, number>;
    for (const item of inWindow) counts[item.kind] += 1;
    return counts;
  }, [inWindow]);
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    for (const item of inWindow) {
      if (!kindShown(filter, item.kind)) continue;
      const list = map.get(item.day) ?? [];
      list.push(item);
      map.set(item.day, list);
    }
    return map;
  }, [inWindow, filter]);
  const oneKind = kinds.size === 1;
  const maxChips = oneKind ? MAX_CHIPS_ONE_KIND : MAX_CHIPS;
  const filtered = kinds.size > 0 || query.trim() !== '';
  // Agenda only (CSS hides the control and the fold at md and up).
  const [showEarlier, setShowEarlier] = useState(false);
  // Only the default window folds: a week picked with the arrows shows whole.
  const foldEarlier = weekOffset === 0;
  const earlierCount = useMemo(() => {
    let n = 0;
    for (const [day, list] of byDay) if (day < today) n += list.length;
    return n;
  }, [byDay, today]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3 pb-2">
        <h2 className="font-display text-[15px] font-bold">Calendar</h2>
        <span className="text-[13px] text-muted-foreground tabular-nums">{fmtDate(start)} – {fmtDateLong(end)}</span>
        <div className="ml-auto inline-flex gap-1.5">
          <Button variant="outline" size="icon" className={NAV_BUTTON} aria-label="Previous week" onClick={() => update({ weekOffset: weekOffset - 1 })}>
            <ChevronLeft />
          </Button>
          <Button variant="outline" size="sm" className={NAV_BUTTON} disabled={weekOffset === 0} onClick={() => update({ weekOffset: 0 })}>
            Today
          </Button>
          <Button variant="outline" size="icon" className={NAV_BUTTON} aria-label="Next week" onClick={() => update({ weekOffset: weekOffset + 1 })}>
            <ChevronRight />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pb-2.5">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Kinds shown">
          <button
            type="button"
            className={cn(PILL, 'border-foreground/60 bg-foreground/[.06] px-[10px] text-foreground')}
            aria-pressed={kinds.size === 0}
            onClick={() => update({ kinds: new Set() })}
          >
            All
          </button>
          {universe.map((kind) => {
            const Icon = KIND_ICONS[kind];
            return (
              <Hint key={kind} content="Shift-click to combine" side="bottom" delayDuration={800}>
                <button
                  type="button"
                  className={cn(PILL, 'border-(--chip-line) bg-(--chip-surface) text-(--chip-ink)', KIND_TONE[kind])}
                  data-kind={kind}
                  aria-pressed={kindShown(filter, kind)}
                  onClick={(e) => update({ kinds: pickKind(kinds, kind, e.shiftKey || e.metaKey || e.ctrlKey, universe) })}
                >
                  <Icon aria-hidden="true" className="size-3 shrink-0" />
                  <span>{KIND_LABELS[kind]}</span>
                  <span className="opacity-75 tabular-nums">{kindCounts[kind]}</span>
                </button>
              </Hint>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-1.5 max-md:ml-0 max-md:w-full">
          <div className="relative max-md:flex-1">
            <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={query}
              onChange={(e) => update({ query: e.target.value })}
              placeholder="Vendor or release"
              aria-label="Filter by vendor or release"
              className="h-[30px] w-56 pl-8 text-[13px] max-md:w-full [&::-webkit-search-cancel-button]:hidden"
            />
          </div>
          {filtered && (
            <Button variant="ghost" size="sm" className={cn(NAV_BUTTON, 'text-muted-foreground')} onClick={() => update({ kinds: new Set(), query: '' })}>
              <X />
              Clear
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-7 border-y border-border bg-muted max-md:hidden" aria-hidden="true">
        {WEEKDAYS.map((d) => <span key={d} className="px-2 py-1.5 text-[11px] font-bold tracking-[.06em] uppercase text-muted-foreground">{d}</span>)}
      </div>
      {foldEarlier && (
        <button
          type="button"
          className="flex w-full items-center gap-1.5 border-t border-border px-3 py-2.5 text-left text-[12px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
          aria-expanded={showEarlier}
          onClick={() => setShowEarlier((v) => !v)}
        >
          <ChevronUp aria-hidden="true" className={cn('size-3.5 transition-transform', showEarlier && 'rotate-180')} />
          {showEarlier ? 'Hide earlier days' : `Earlier · since ${fmtDate(start)}`}
          <span className="ml-auto tabular-nums opacity-75">{earlierCount}</span>
        </button>
      )}
      <div className="@container grid grid-cols-7 max-md:grid-cols-1 max-md:border-t max-md:border-border">
        {days.map((day, index) => {
          const dayItems = byDay.get(day) ?? [];
          // Folding a single chip behind "+1 more" hides one to show one.
          const showAll = dayItems.length <= maxChips + 1;
          const visible = showAll ? dayItems : dayItems.slice(0, maxChips);
          const monthEdge = index === 0 || day.endsWith('-01');
          const isToday = day === today;
          return (
            <div
              key={day}
              className="flex min-h-[108px] min-w-0 flex-col gap-[3px] border-r border-b border-border p-1.5 nth-[7n]:border-r-0 nth-last-[-n+7]:border-b-0 data-[past]:bg-muted/60 max-md:min-h-0 max-md:border-r-0 max-md:px-3 max-md:py-2 max-md:nth-last-[-n+7]:not-last:border-b max-md:data-[empty]:not-data-[today]:hidden max-md:data-[folded]:hidden"
              data-today={isToday || undefined}
              data-past={day < today || undefined}
              data-empty={dayItems.length === 0 || undefined}
              data-folded={(foldEarlier && !showEarlier && day < today) || undefined}
            >
              <div className="mb-0.5 flex items-center">
                <span className={cn('inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[12px] font-semibold text-muted-foreground tabular-nums max-md:hidden', isToday && 'bg-primary text-primary-foreground')}>{monthEdge ? fmtDate(day) : Number(day.slice(8))}</span>
                <span className={cn('hidden text-[12px] font-semibold max-md:block', isToday && 'text-primary')}>{format(dayDate(day), 'EEEE, MMM d')}</span>
              </div>
              {visible.map((item) => <Chip key={item.key} item={item} returnTo={returnTo} readOnly={readOnly} />)}
              {!showAll && <DayMore day={day} items={dayItems} rest={dayItems.length - maxChips} returnTo={returnTo} readOnly={readOnly} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
